import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ 
  baseURL: API,
  withCredentials: true
});

// Attach Authorization header if token exists in localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("alaira_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Bypass-Tunnel-Reminder"] = "true";
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Map backend product representation to frontend expected model
export const mapProduct = (p) => {
  if (!p) return null;
  const id = p._id || p.id;
  const image = p.mainImage?.url || p.images?.[0]?.url || p.image || "";
  const images = p.images?.map(img => typeof img === 'string' ? img : img.url) || [image];
  const original_price = p.price;
  const price = p.discountPrice || p.price;
  const discount_pct = p.price > price ? Math.round(((p.price - price) / p.price) * 100) : 0;
  const on_sale = discount_pct > 0;
  const reviews_count = p.reviews?.length || p.reviews_count || 0;
  const name = p.title || p.name || "";
  const rating = p.ratings?.average || p.rating || 5.0;
  
  const reviews = p.reviews?.map(r => ({
    author: r.user?.name || "Anonymous",
    avatar: r.user?.avatar || "",
    rating: r.rating || 5,
    comment: r.comment || "",
    body: r.comment || "",
    date: r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "Recently"
  })) || [];
  
  const videos = p.videos?.map(v => typeof v === 'string' ? v : v.url) || [];
  const declaration = p.declaration || "Certified Authentic Alaira Premium Quality Gift.";
  const shippingReturns = p.shippingReturns || "Ships within 24-48 hours. Returns accepted within 14 days under original wrapper conditions.";
  const faqs = p.faqs && p.faqs.length ? p.faqs : [
    { question: "Is this wrapped ready for gifting?", answer: "Yes, every Alaira order comes with our signature presentation wrap and a blank matching greeting card." }
  ];

  return {
    id,
    name,
    title: name,
    price,
    original_price,
    discount_pct,
    on_sale,
    image,
    images,
    videos,
    category: p.category,
    description: p.description,
    declaration,
    shippingReturns,
    faqs,
    fabric: p.fabric || "Soft Cotton",
    occasion: p.occasion || "Anniversary",
    color: p.color || "Multi",
    stock: p.stock || 10,
    rating,
    reviews_count,
    reviews,
    delivery_days: p.delivery_days || "3-5 days",
    badge: p.featured ? "Featured" : "",
    highlights: p.highlights || [
      "Premium luxury materials",
      "Handcrafted with love",
      "Signature presentation wrapping included"
    ]
  };
};

export const DEFAULT_CATEGORIES = [
  { name: "Toys", slug: "toys", image: "https://images.unsplash.com/photo-1559454403-b8fb88521f11?w=400", count: 42, tint: "#F4F0FF" },
  { name: "Jewelry", slug: "jewelry", image: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400", count: 29, tint: "#FFF4F7" },
  { name: "Flowers", slug: "flowers", image: "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?w=400", count: 8, tint: "#EAF5FF" },
  { name: "Chocolates", slug: "sweets", image: "/sweets/IMG_4057.JPG.jpeg", count: 15, tint: "#FFF0F3" }
];

export const getCategories = () => 
  api.get("/categories")
    .then((r) => {
      const data = r.data || [];
      const hasChocolates = data.some(c => c.slug === "sweets" || c.slug === "chocolates" || c.name?.toLowerCase() === "chocolates");
      if (!hasChocolates) {
        return [
          ...data,
          { name: "Chocolates", slug: "sweets", image: "/sweets/IMG_4057.JPG.jpeg", count: 15, tint: "#FFF0F3" }
        ];
      }
      return data;
    })
    .catch(() => DEFAULT_CATEGORIES);

export const getProducts = (params = {}) => {
  const queryParams = { ...params };
  if (queryParams.category && (queryParams.category.toLowerCase() === "sweets" || queryParams.category.toLowerCase() === "chocolate")) {
    queryParams.category = "Chocolates";
  }
  return api.get("/products", { params: queryParams }).then((r) => {
    const list = r.data.products || r.data;
    return Array.isArray(list) ? list.map(mapProduct) : [];
  });
};

export const getProduct = (id) => 
  api.get(`/products/${id}`).then((r) => {
    const prod = r.data.product || r.data;
    return mapProduct(prod);
  });

export const getCombos = () => api.get("/combos").then((r) => r.data);

export const createOrder = (payload) => api.post("/orders", payload).then((r) => r.data);

export const subscribeNewsletter = (email) => api.post("/newsletter", { email }).then((r) => r.data);

// Authentication actions
export const loginUser = (payload) => 
  api.post("/users/login", payload).then((r) => {
    if (r.data?.token) {
      localStorage.setItem("alaira_token", r.data.token);
    }
    return r.data;
  });

export const registerUser = (payload) => 
  api.post("/users/register", payload).then((r) => {
    if (r.data?.token) {
      localStorage.setItem("alaira_token", r.data.token);
    }
    return r.data;
  });

export const getCurrentUser = () => 
  api.get("/users/me").then((r) => r.data);

export const logoutUser = () => 
  api.post("/users/logout").then((r) => {
    localStorage.removeItem("alaira_token");
    return r.data;
  }).catch((err) => {
    localStorage.removeItem("alaira_token");
    throw err;
  });

export const submitProductReview = (id, payload) => 
  api.post(`/products/${id}/reviews`, payload).then((r) => {
    return {
      ratings: r.data.ratings,
      reviews: r.data.reviews?.map(rv => ({
        author: rv.user?.name || "Anonymous",
        avatar: rv.user?.avatar || "",
        rating: rv.rating || 5,
        comment: rv.comment || "",
        body: rv.comment || "",
        date: rv.createdAt ? new Date(rv.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "Recently"
      })) || []
    };
  });

export const getMyOrders = () => 
  api.get("/orders/me").then((r) => r.data.orders || r.data);

export const loginWithFirebaseToken = (idToken) => 
  api.post("/users/auth/firebase", {}, {
    headers: { Authorization: `Bearer ${idToken}` }
  }).then((r) => {
    if (r.data?.token) {
      localStorage.setItem("alaira_token", r.data.token);
    }
    return r.data;
  });

// Admin Panel endpoints
export const getAdminStats = () => api.get("/admin/stats").then((r) => r.data);
export const getAdminOrders = () => api.get("/orders").then((r) => r.data.orders || r.data);
export const updateOrderStatus = (id, deliveryStatus) => api.patch(`/orders/${id}/status`, { deliveryStatus }).then((r) => r.data);
export const getCoupons = () => api.get("/coupons").then((r) => r.data.coupons || r.data);
export const createCoupon = (payload) => api.post("/coupons", payload).then((r) => r.data);
export const deleteCoupon = (id) => api.delete(`/coupons/${id}`).then((r) => r.data);
export const createProduct = (payload) => api.post("/products", payload).then((r) => r.data);
export const updateProduct = (id, payload) => api.put(`/products/${id}`, payload).then((r) => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then((r) => r.data);


